/**
 * Utilitários de sanitização para proteger contra scripts maliciosos e caracteres especiais
 */

/**
 * Remove ou escapa caracteres HTML perigosos
 */
export const sanitizeHtml = (text: string): string => {
  if (!text) return '';
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Remove scripts maliciosos e tags HTML perigosas
 */
export const removeScripts = (text: string): string => {
  if (!text) return '';
  
  return text
    // Remove tags script
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove tags style
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    // Remove eventos JavaScript
    .replace(/\bon\w+\s*=\s*["'][^"']*["']/gi, '')
    // Remove javascript: URLs
    .replace(/javascript:/gi, '')
    // Remove data: URLs suspeitos
    .replace(/data:text\/html/gi, '')
    // Remove vbscript: URLs
    .replace(/vbscript:/gi, '');
};

/**
 * Limita o comprimento do texto
 */
export const limitLength = (text: string, maxLength: number = 1000): string => {
  if (!text) return '';
  
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  
  return text;
};

/**
 * Remove caracteres de controle e caracteres especiais problemáticos
 */
export const removeControlCharacters = (text: string): string => {
  if (!text) return '';
  
  return text
    // Remove caracteres de controle (exceto quebras de linha e tabs)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Remove caracteres Unicode problemáticos
    .replace(/[\uFEFF\u200B-\u200D\u2060]/g, '')
    // Normaliza quebras de linha
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
};

/**
 * Sanitiza texto para exibição segura
 */
export const sanitizeText = (text: string, options: {
  maxLength?: number;
  allowHtml?: boolean;
  allowLineBreaks?: boolean;
} = {}): string => {
  if (!text) return '';
  
  const { maxLength = 1000, allowHtml = false, allowLineBreaks = true } = options;
  
  let sanitized = text;
  
  // Remove caracteres de controle
  sanitized = removeControlCharacters(sanitized);
  
  // Remove scripts se HTML não for permitido
  if (!allowHtml) {
    sanitized = removeScripts(sanitized);
    sanitized = sanitizeHtml(sanitized);
  }
  
  // Processa quebras de linha
  if (allowLineBreaks) {
    sanitized = sanitized.replace(/\n/g, '<br>');
  }
  
  // Limita comprimento
  sanitized = limitLength(sanitized, maxLength);
  
  return sanitized;
};

/**
 * Valida se o texto contém apenas caracteres seguros
 */
export const isValidText = (text: string): boolean => {
  if (!text) return true;
  
  // Verifica se contém scripts
  const hasScripts = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi.test(text);
  if (hasScripts) return false;
  
  // Verifica se contém eventos JavaScript
  const hasEvents = /\bon\w+\s*=/gi.test(text);
  if (hasEvents) return false;
  
  // Verifica se contém URLs maliciosas
  const hasMaliciousUrls = /(javascript:|vbscript:|data:text\/html)/gi.test(text);
  if (hasMaliciousUrls) return false;
  
  // Verifica comprimento
  if (text.length > 2000) return false;
  
  return true;
};

/**
 * Sanitiza texto para armazenamento no banco de dados
 */
export const sanitizeForDatabase = (text: string): string => {
  if (!text) return '';
  
  return text
    // Remove caracteres de controle
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Remove caracteres Unicode problemáticos
    .replace(/[\uFEFF\u200B-\u200D\u2060]/g, '')
    // Normaliza quebras de linha
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Limita comprimento
    .substring(0, 2000);
};
